class ProductInformation extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    this.querySelector('pinfo-variant-picker')?.addEventListener(
      'input',
      this.handleVariantEvent.bind(this)
    );
    this.initQuantitySelector();
    this.initSwiper();
    this.initSellingPlan();
    this.initAccordion();
    this.bindFormSubmit();
    this.bindBuyNow();
  }

  // product | json — used for media, variant picker, price block
  get productJSON() {
    if (!this._productJSON) {
      const el = this.querySelector('script[data-product-json]');
      this._productJSON = el ? JSON.parse(el.textContent) : null;
    }
    return this._productJSON;
  }

  // Build allocation lookup from productJSON.
  // product | json DOES include selling_plan_allocations on each variant
  // (confirmed from products/<handle>.js output).
  // Structure: { [variantId]: { price, allocations: { [spId]: { price, compare_at_price } } } }
  get spAllocations() {
    if (!this._spAllocations) {
      // Primary: build from productJSON (always available on product pages)
      const product = this.productJSON;
      if (product?.variants) {
        const variants = {};
        product.variants.forEach(v => {
          const allocations = {};
          (v.selling_plan_allocations || []).forEach(a => {
            allocations[String(a.selling_plan_id)] = {
              price: a.price,
              compare_at_price: a.compare_at_price
            };
          });
          variants[String(v.id)] = { price: v.price, allocations };
        });
        this._spAllocations = { variants };
      } else {
        // Fallback: read from the embedded #sp-allocations-json script tag
        const el = document.getElementById('sp-allocations-json');
        try {
          this._spAllocations = el ? JSON.parse(el.textContent) : { variants: {} };
        } catch (e) {
          console.warn('sp-allocations-json parse error', e);
          this._spAllocations = { variants: {} };
        }
      }
    }
    return this._spAllocations;
  }

  get mediaOrder() {
    if (!this._mediaOrder) {
      this._mediaOrder = Array.from(
        this.querySelectorAll('.main-swiper .swiper-slide')
      ).map(s => s.dataset.media);
    }
    return this._mediaOrder;
  }

  handleVariantEvent(e) {
    const { variantUrl } = e.target.dataset;
    this.handleOptions(variantUrl);
    const params = new URLSearchParams(variantUrl.split('?')[1]);
    const variantId = Number(params.get('variant'));
    if (variantId) this.slideToVariantMedia(variantId);
  }

  slideToVariantMedia(variantId) {
    const product = this.productJSON;
    if (!product) return;
    const variant = product.variants.find(v => v.id === variantId);
    if (!variant || !variant.featured_media) return;
    const mediaId = String(variant.featured_media.id);
    const idx = this.mediaOrder.indexOf(mediaId);
    if (idx > -1 && this.mainSwiper) this.mainSwiper.slideTo(idx, 300);
  }

  // ─── Quantity Selector ───────────────────────────────────────────
  initQuantitySelector() {
    const wrapper = this.querySelector('[data-quantity-selector]');
    if (!wrapper) return;
    const input = wrapper.querySelector('.qty-input');
    const dec = wrapper.querySelector('.decrease');
    const inc = wrapper.querySelector('.increase');
    const update = () => this.dispatchQuantityChange(input.value);
    dec?.addEventListener('click', () => {
      let v = parseInt(input.value) || 1;
      if (v > (parseInt(input.min) || 1)) { input.value = v - 1; update(); }
    });
    inc?.addEventListener('click', () => {
      input.value = (parseInt(input.value) || 1) + 1;
      update();
    });
    input?.addEventListener('input', () => {
      if (input.value < 1) input.value = 1;
      update();
    });
  }

  dispatchQuantityChange(value) {
    this.dispatchEvent(new CustomEvent('quantity-change', {
      detail: { quantity: Number(value) },
      bubbles: true
    }));
  }

  // ─── Swiper Init ─────────────────────────────────────────────────
  initSwiper() {
    const container = this.querySelector('.pdp-media-container');
    if (!container) return;
    const mainEl = container.querySelector('.main-swiper');
    const thumbEl = container.querySelector('.thumb-swiper');
    if (!mainEl || !thumbEl) return;

    this.thumbSwiper = new Swiper(thumbEl, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchSlidesProgress: true,
      freeMode: true,
      direction: 'horizontal',
    });

    this.mainSwiper = new Swiper(mainEl, {
      thumbs: { swiper: this.thumbSwiper },
      loop: false,
      on: {
        slideChange: () => this.updateNavButtons(),
        init: () => this.updateNavButtons(),
      }
    });

    const prevBtn = mainEl.querySelector('.swiper-nav-prev');
    const nextBtn = mainEl.querySelector('.swiper-nav-next');
    prevBtn?.addEventListener('click', () => this.mainSwiper.slidePrev(300));
    nextBtn?.addEventListener('click', () => this.mainSwiper.slideNext(300));
    this._prevBtn = prevBtn;
    this._nextBtn = nextBtn;
  }

  updateNavButtons() {
    if (!this.mainSwiper) return;
    if (this._prevBtn) this._prevBtn.classList.toggle('swiper-button-disabled', this.mainSwiper.isBeginning);
    if (this._nextBtn) this._nextBtn.classList.toggle('swiper-button-disabled', this.mainSwiper.isEnd);
  }

  // ─── Selling Plan ─────────────────────────────────────────────────
  initSellingPlan() {
    const widget = this.querySelector('[data-selling-plan-widget]');
    if (!widget) return;

    const hiddenInput = widget.querySelector('#pinfo-selling-plan-input');

    // Get current variant id string from form hidden input
    const getVariantId = () => {
      const idInput = this.querySelector('input[name="id"]');
      return idInput ? String(idInput.value) : null;
    };

    // Sync price, compare, badge display for a given selling plan id
    const syncDisplay = (spId, priceEl, compareEl, badgeEl, freqSelect) => {
      const variantId   = getVariantId();
      const variantData = variantId ? (this.spAllocations.variants[variantId] || null) : null;
      const alloc       = (variantData && spId) ? (variantData.allocations[String(spId)] || null) : null;

      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      // Badge from <option> data-badge attribute
      if (badgeEl && freqSelect) {
        const opt   = freqSelect.options[freqSelect.selectedIndex];
        const badge = opt ? (opt.dataset.badge || '') : '';
        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    };

    widget.querySelectorAll('.sp-widget').forEach(group => {
      const cards         = group.querySelectorAll('.sp-card');
      const subscribeCard = group.querySelector('.sp-card--subscribe');
      const freqSelect    = group.querySelector('[data-frequency-select]');
      const badgeEl       = group.querySelector('[data-badge-display]');
      const priceEl       = group.querySelector('[data-subscribe-price-display]');
      const compareEl     = group.querySelector('[data-subscribe-compare-display]');

      // ── Card click ──
      cards.forEach(card => {
        card.addEventListener('click', () => {
          cards.forEach(c => {
            c.classList.remove('active');
            c.querySelector('.sp-hidden-radio').checked = false;
          });
          card.classList.add('active');
          card.querySelector('.sp-hidden-radio').checked = true;

          if (card.dataset.spCard === 'subscribe' && freqSelect) {
            const opt  = freqSelect.options[freqSelect.selectedIndex];
            const spId = opt ? opt.value : '';
            if (hiddenInput) hiddenInput.value = spId;
            syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
          } else {
            if (hiddenInput) hiddenInput.value = '';
          }
        });
      });

      // ── Frequency select change ──
      if (freqSelect) {
        freqSelect.addEventListener('change', () => {
          const opt  = freqSelect.options[freqSelect.selectedIndex];
          const spId = opt ? opt.value : '';
          if (subscribeCard?.classList.contains('active') && hiddenInput) {
            hiddenInput.value = spId;
          }
          syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
        });

        // Prevent dropdown click from firing the card click handler
        freqSelect.addEventListener('click', e => e.stopPropagation());

        // ── Initial display on page load ──
        const initOpt = freqSelect.options[freqSelect.selectedIndex];
        if (initOpt) syncDisplay(initOpt.value, priceEl, compareEl, badgeEl, freqSelect);
      }
    });

    if (hiddenInput) hiddenInput.value = '';
  }

  // ─── Update selling plan prices when variant changes ──────────────
  // Reads from #sp-allocations-json (Liquid-embedded, per variant).
  updateSellingPlanPrices(variantId) {
    const variantData = this.spAllocations.variants[String(variantId)];

    // 1. One-time price
    const oneTimePriceEl = this.querySelector('[data-one-time-price]');
    if (oneTimePriceEl && variantData) {
      oneTimePriceEl.textContent = this.formatMoney(variantData.price);
    }

    // 2. Rebuild plan map from productJSON for badge recomputation (price-type plans)
    const planMap = {};
    (this.productJSON?.selling_plan_groups || []).forEach(g =>
      (g.selling_plans || []).forEach(p => { planMap[p.id] = p; })
    );

    // 3. Re-sync each subscribe widget
    this.querySelectorAll('.sp-widget').forEach(groupEl => {
      const freqSelect = groupEl.querySelector('[data-frequency-select]');
      const badgeEl    = groupEl.querySelector('[data-badge-display]');
      const priceEl    = groupEl.querySelector('[data-subscribe-price-display]');
      const compareEl  = groupEl.querySelector('[data-subscribe-compare-display]');
      if (!freqSelect) return;

      const opt    = freqSelect.options[freqSelect.selectedIndex];
      const spId   = opt ? String(opt.value) : null;
      const alloc  = (variantData && spId) ? (variantData.allocations[spId] || null) : null;

      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      // Recompute badge for 'price' type adjustments (savings vary by variant)
      if (badgeEl && opt && variantData) {
        const plan = planMap[Number(spId)];
        const adj  = plan?.price_adjustments?.[0];
        let badge  = opt.dataset.badge || '';

        if (adj?.value_type === 'price') {
          const saving = variantData.price - adj.value;
          badge = saving > 0 ? 'Save ' + this.formatMoney(saving) : 'Special Price';
          opt.dataset.badge = badge;
        }

        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    });
  }

  // ─── Format money ─────────────────────────────────────────────────
  // Mirrors Liquid's money_without_trailing_zeros.
  // Uses Shopify.formatMoney if available, Intl.NumberFormat as fallback.
  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';

    if (window.Shopify?.formatMoney) {
      const fmt = window.Shopify.money_format
        || window.Shopify.moneyFormat
        || window.theme?.moneyFormat
        || '${{amount}}';
      const raw = window.Shopify.formatMoney(cents, fmt);
      // Strip trailing .00 or ,00 (not .50 or .99)
      return raw.replace(/[.,]00(?!\d)/, '');
    }

    // Intl fallback
    const amount   = cents / 100;
    const hasCents = (amount % 1) !== 0;
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  }

  // ─── Accordion ────────────────────────────────────────────────────
  initAccordion() {
    const accordion = this.querySelector('.pinfo-accordion');
    if (!accordion) return;

    accordion.querySelectorAll('.accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        const content = trigger.nextElementSibling;

        accordion.querySelectorAll('.accordion-trigger').forEach(t => {
          t.setAttribute('aria-expanded', 'false');
          t.nextElementSibling?.classList.remove('open');
        });

        if (!isOpen) {
          trigger.setAttribute('aria-expanded', 'true');
          content?.classList.add('open');
        }
      });
    });
  }

  // ─── Form Bindings ────────────────────────────────────────────────
  bindFormSubmit() {
    const form = this.querySelector('form[action$="/cart/add"]');
    if (!form) return;
    form.removeEventListener('submit', this._boundHandleAddToCart);
    this._boundHandleAddToCart = this.handleAddToCart.bind(this);
    form.addEventListener('submit', this._boundHandleAddToCart);
  }

  bindBuyNow() {
    const btn = this.querySelector('.p-info-btn-buy-now');
    if (!btn) return;
    btn.removeEventListener('click', this._boundHandleBuyNow);
    this._boundHandleBuyNow = this.handleBuyNow.bind(this);
    btn.addEventListener('click', this._boundHandleBuyNow);
  }

  handleBuyNow = async (e) => {
    e.preventDefault();
    const btn = e.currentTarget;
    const form = this.querySelector('form[action$="/cart/add"]');
    if (!form) return;

    btn.disabled = true;
    btn.textContent = 'Processing...';

    const data = new FormData(form);
    const qty = this.querySelector('.qty-input');
    if (qty) data.set('quantity', qty.value);
    this.appendSellingPlan(data);

    try {
      await fetch('/cart/add.js', { method: 'POST', body: data });
      window.location.href = '/checkout';
    } catch (err) {
      console.error('Buy Now failed:', err);
      btn.textContent = 'Try Again';
      btn.disabled = false;
    }
  }

  appendSellingPlan(formData) {
    const hiddenInput = this.querySelector('#pinfo-selling-plan-input');
    if (hiddenInput && hiddenInput.value) {
      formData.set('selling_plan', hiddenInput.value);
    } else {
      formData.delete('selling_plan');
    }
  }

  handleOptions(url) {
    this.fetchData(url);
    this.updateURL(url);
  }

  async fetchData(url) {
    const sectionUrl = `${url}&section_id=${this.sectionId}`;
    const resp = await fetch(sectionUrl);
    const txt = await resp.text();
    const html = new DOMParser().parseFromString(txt, 'text/html');
    this.renderEl(html);
  }

  renderEl(html) {
    this.querySelectorAll('pinfo-variant-picker').forEach((el, i) => {
      const newEl = html.querySelectorAll('pinfo-variant-picker')[i];
      if (newEl) el.innerHTML = newEl.innerHTML;
    });
    this.querySelectorAll('.pinfo-price-wrapper').forEach((el, i) => {
      const newEl = html.querySelectorAll('.pinfo-price-wrapper')[i];
      if (newEl) el.innerHTML = newEl.innerHTML;
    });
    this.querySelectorAll('.pinfo-form-wrapper').forEach((el, i) => {
      const newEl = html.querySelectorAll('.pinfo-form-wrapper')[i];
      if (newEl) {
        el.innerHTML = newEl.innerHTML;
        this.bindFormSubmit();
        this.bindBuyNow();
      }
    });

    const idInput = this.querySelector('input[name="id"]');
    if (idInput?.value) {
      const vid = Number(idInput.value);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId: vid } }));
      this.slideToVariantMedia(vid);
      this.updateSellingPlanPrices(vid);
    }
  }

  updateURL(url) {
    if (typeof url !== 'string' || !url.startsWith('/')) return;
    if (this.dataset?.updateUrl === 'true') {
      window.history.pushState({}, '', url);
    }
  }

  handleAddToCart = (e) => {
    e.preventDefault();
    const btn = this.querySelector('button[type="submit"]');
    const originalText = btn?.textContent || 'Add to Bag';
    if (btn) {
      btn.textContent = 'Adding...';
      btn.disabled = true;
      btn.style.opacity = '0.75';
    }
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const form = this.querySelector('form[action$="/cart/add"]');
    const data = new FormData(form);
    const qty = this.querySelector('.qty-input');
    if (qty) data.set('quantity', qty.value);
    this.appendSellingPlan(data);

    if (this.cart) data.append('sections', this.cart.getSectionsToRender().map(s => s.id));
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: data
    })
      .then(r => r.json())
      .then(res => { if (this.cart) this.cart.renderContents(res); })
      .finally(() => {
        if (btn) {
          btn.textContent = originalText;
          btn.disabled = false;
          btn.style.opacity = '1';
        }
        if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        this.closest('.pqv-wrapper')?.querySelector('.pqv-close-btn')?.click();
      });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  customElements.define('product-information', ProductInformation);
});
